/**
 * The section's sentences that need both a rule from `lib/monitoring` and the
 * catalogue: what a check code says, how long an outage lasted, how far off an
 * expiry is.
 *
 * Kept apart from the components because four of them print the same few
 * things (the table, the site page, the incident list and the add dialog's
 * first-check line), and a copy in each is how "27 min" ends up "27min" on one
 * screen.
 */
import {
  codeMessage,
  expiryKey,
  formatDuration,
  notChecked,
  type MonitorExpiryView,
  type MonitorRow,
} from "../../../lib/monitoring";

export type T = (key: string, vars?: Record<string, string | number>) => string;

/** A check code as a sentence, or the code itself when this browser does not
 *  know it - a raw code on screen is a bug report, a blank is a mystery. */
export function codeText(t: T, code: string | null | undefined): string | null {
  if (!code) return null;
  const msg = codeMessage(code);
  return msg ? t(msg.key, msg.vars) : code;
}

export function durationText(t: T, seconds: number): string {
  return formatDuration(seconds, {
    d: t("monitoring.unit.d"),
    h: t("monitoring.unit.h"),
    min: t("monitoring.unit.min"),
  });
}

/** "in 12 days", "today", "expired 3 days ago", "not known yet". */
export function expiryText(t: T, view: MonitorExpiryView | null): string {
  const key = expiryKey(view);
  if (key === "inDays") return t("monitoring.expiry.inDays", { n: view!.daysLeft! });
  if (key === "expired") return t("monitoring.expiry.expired", { n: Math.abs(view!.daysLeft!) });
  return t(`monitoring.expiry.${key}`);
}

/**
 * The line under a row's pill: why it is in that state, or null for up and
 * for a site that is not checked (paused, or its client inactive).
 *
 * While a confirmed outage is open the line says how long, and what the
 * outage's own failing checks said - not whatever the latest check said. The
 * pill stays "down" through a blocked or unknown check in between, and the
 * reason the desk was alerted is the one worth reading. Any other state
 * (including a site never seen working) shows its own last code.
 */
export function stateLine(t: T, row: MonitorRow): string | null {
  if (notChecked(row)) return null;
  const inc = row.openIncident;
  if (inc) {
    const time = durationText(t, inc.durationSeconds);
    const why = codeText(t, inc.code);
    return why ? `${time} · ${why}` : time;
  }
  if (row.state === "up") return null;
  return codeText(t, row.lastCode);
}
